import { useSelector, useDispatch } from 'react-redux'
import Modal from '@ui/component/Modal'
import Form from '@ui/elements/Form'
import useToggle from '@hook/useToggle'
import { citySaveUrl, cityFormFields } from '@form/cityFormFields'
import { mairieSaveUrl, mairieFormFields } from '@form/mairieFormFields'
import { districtSaveUrl, districtFormFields } from '@form/districtFormFields'
import { documentSaveUrl, documentFormFields } from '@form/documentFormFields'
import { userRegisterSaveUrl, userRegisterFormFields } from '@form/userRegisterFormFields'
import { missionContactSaveUrl, missionContactFormFields } from '@form/missionContactFormFields'
import { missionBillingSaveUrl, missionBillingFormFields } from '@form/missionBillingFormFields'
import { missionSubjectSaveUrl, missionSubjectFormFields } from '@form/missionSubjectFormFields'
import { pageSaveUrl, pageFormFields } from '@form/pageFormFields'
import { mailTemplateSaveUrl, mailTemplateFormFields } from '@form/mailTemplateFormFields'
import { loadCallsSaveUrl, loadCallsFormFields } from '@form/loadCallsFormFields'
import { userRoleSaveUrl, userRoleFormFields } from '@form/userRoleFormFields'
import { billingTypeSaveUrl, billingTypeFormFields } from '@form/billingTypeFormFields'
import { permissionSubjectSaveUrl, permissionSubjectFormFields } from '@form/permissionSubjectFormFields'
import { updateDynamicFormObject, initDynamicForm, updateData } from '@store/slice/mainSlice'
import { addMissionContact } from '@store/slice/missionSlice'
import { addMissionBilling } from '@store/slice/missionBillingSlice'
import { updateDistricts } from '@store/slice/districtsSlice'
import { addDocument } from '@store/slice/documentsSlice'
import { addPage } from '@store/slice/cmsSlice'
import { addMailTemplate } from '@store/slice/mailTemplatesSlice'
import { addSterilizationCalls } from '@store/slice/sterilizationSlice'
import { addUserRole } from '@store/slice/permissionsSlice'
import { useEffect } from 'react'
import { post } from '@util/xhrAPI'
import { useTranslation } from 'react-i18next'
import useStoreUpdate from '@hook/useStoreUpdate'
import useFormData from '@hook/useFormData'
import Div from '@ui/layout/Div'


const DynamicForm = () => {
  const [t] = useTranslation('form')
  const { dynamicFormType, dynamicStore, dynamicFormObject, data } = useSelector(state => state.main)
  const { districts } = useSelector(state => state.districts)
  const { pageTypes } = useSelector(state => state.cms)
  const { mailTemplateTypes } = useSelector(state => state.mailTemplates)
  const dispatch = useDispatch()
  const [isModalActive, toggleModal] = useToggle(false)
  const [isLoading, toggleLoading] = useToggle(false)
  const [updateFormObject, updateFormObjectProp] = useStoreUpdate(dispatch, updateDynamicFormObject, dynamicFormObject)
  const renderFormData = useFormData()
  let saveUrl = ''

  let addittionallCallback = false
  const generateForm = () => {
    switch (dynamicFormType) {
      case 'city':
        saveUrl = citySaveUrl
        return cityFormFields(dynamicFormObject, updateFormObject, saveForm, [], isLoading, t)
      case 'missionSubject':
        saveUrl = missionSubjectSaveUrl
        return missionSubjectFormFields(dynamicFormObject, updateFormObject, saveForm, [], isLoading, t)
      case 'mairie':
        saveUrl = mairieSaveUrl
        return mairieFormFields(dynamicFormObject, updateFormObject, saveForm, [], isLoading, t)
      case 'district':
        saveUrl = districtSaveUrl
        addittionallCallback = district => dispatch(updateDistricts({ ...districts, [district.id]: district }))
        return districtFormFields(dynamicFormObject, updateFormObject, saveForm, [], isLoading, t)
      case 'document':
        saveUrl = documentSaveUrl
        addittionallCallback = document => dispatch(addDocument(document))
        return documentFormFields(dynamicFormObject, updateFormObject, updateFormObjectProp, saveForm, [], isLoading, t)
      case 'missionContact':
        saveUrl = missionContactSaveUrl
        addittionallCallback = missionContact => dispatch(addMissionContact(missionContact))
        return missionContactFormFields(dynamicFormObject, updateFormObject, saveForm, [], isLoading, t)
      case 'missionBilling':
        saveUrl = missionBillingSaveUrl
        addittionallCallback = missionBilling => dispatch(addMissionBilling(missionBilling))
        return missionBillingFormFields(dynamicFormObject, updateFormObject, saveForm, [], isLoading, t)
      case 'userRegister':
        saveUrl = userRegisterSaveUrl
        return userRegisterFormFields(dynamicFormObject, updateFormObject, updateFormObjectProp, saveForm, [], isLoading, t, data)
      case 'loadCalls':
        saveUrl = loadCallsSaveUrl
        addittionallCallback = callsList => dispatch(addSterilizationCalls(callsList))
        return loadCallsFormFields(dynamicFormObject, updateFormObject, updateFormObjectProp, saveForm, [], isLoading, t)
      case 'page':
        saveUrl = pageSaveUrl
        addittionallCallback = page => dispatch(addPage(page))
        return pageFormFields(dynamicFormObject, updateFormObject, updateFormObjectProp, saveForm, [], isLoading, t, pageTypes)
      case 'mailTemplate':
        saveUrl = mailTemplateSaveUrl
        addittionallCallback = mailTemplate => dispatch(addMailTemplate(mailTemplate))
        return mailTemplateFormFields(dynamicFormObject, updateFormObject, updateFormObjectProp, saveForm, [], isLoading, t, mailTemplateTypes)
      case 'userRole':
        saveUrl = userRoleSaveUrl
        addittionallCallback = userRole => dispatch(addUserRole(userRole))
        return userRoleFormFields(dynamicFormObject, updateFormObject, saveForm, [], isLoading, t)
      case 'permissionSubject':
        saveUrl = permissionSubjectSaveUrl
        return permissionSubjectFormFields(dynamicFormObject, updateFormObject, saveForm, [], isLoading, t)
      case 'billingType':
        saveUrl = billingTypeSaveUrl
        return billingTypeFormFields(dynamicFormObject, updateFormObject, saveForm, [], isLoading, t)
    }
  }

  const saveForm = e => {
    e.preventDefault()
    post(saveUrl, renderFormData(dynamicFormObject), response => {
      toggleLoading()
      if (dynamicStore) { // dynamic store is to update the field data under the main slice don't set dynamic store for separated slices
        dispatch(updateData({ ...data, [dynamicStore]: [...data[dynamicStore], { ...response.option }] }))
      }

      if (addittionallCallback) {
        const store = () => {
          switch (dynamicFormType) {
            case 'loadCalls':
              return 'calls'
            default:
              return dynamicFormType
          }
        }
        addittionallCallback(response[store()])
      }

      dispatch(initDynamicForm())
      toggleLoading()
      toggleModal()
    })
  }

  useEffect(() => {
    if (!isModalActive) {
      dispatch(initDynamicForm())
    }
  }, [isModalActive])

  useEffect(() => {
    if (dynamicFormType) {
      toggleModal()
    }
  }, [dynamicFormType])

  return (
    <Modal cssClass='create-element-modal' isActive={isModalActive} closeCallback={toggleModal} closeAction={true}>
      <h3>{t(`titles.${dynamicFormType}`)}</h3>
      <Form>
        <Div cssClass="columns is-multiline">
          {generateForm()}
        </Div>
      </Form>
    </Modal>
  )
}

export default DynamicForm